import { useState } from "react";
import { useI18n } from "../../../hooks/useTranslations";
import { StoredColumnFilterValue } from "../../../types";
import DatePicker from "../../../ui/DatePicker";
import MonthYearPicker from "../../../ui/MonthYearPicker";
import { Option } from "../HeaderMenuOption";
import Numeric from "../Numeric";
import FilterModeSwitcher from "./FilterModeSwitcher";
import FilterOptionList from "./FilterOptionList";
import FilterRangeInputs from "./FilterRangeInputs";
import FilterValueSlot from "./FilterValueSlot";
import { ColumnFilterProps } from "./types";
import { toggleListOption, useDistinctValues } from "./useDistinctValues";

type Mode = "exact" | "month" | "year" | "range";

const pad2 = (n: number) => String(n).padStart(2, "0");

const lastDayOfMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

const monthSpan = (year: number, month: number): [string, string] => [
  `${year}-${pad2(month)}-01`,
  `${year}-${pad2(month)}-${pad2(lastDayOfMonth(year, month))}`,
];

const yearSpan = (year: number): [string, string] => [`${year}-01-01`, `${year}-12-31`];

const isYearSpan = (start: unknown, end: unknown): boolean => {
  if (typeof start !== "string" || typeof end !== "string") return false;
  const match = /^(\d{4})-01-01$/.exec(start);
  return !!match && end === `${match[1]}-12-31`;
};

const isMonthSpan = (start: unknown, end: unknown): boolean => {
  if (typeof start !== "string" || typeof end !== "string") return false;
  const match = /^(\d{4})-(\d{2})-01$/.exec(start);
  if (!match) return false;
  const [, year, month] = match;
  return end === `${year}-${month}-${pad2(lastDayOfMonth(Number(year), Number(month)))}`;
};

const deriveMode = (selected: StoredColumnFilterValue): Mode => {
  if (selected.operator === "gte" || selected.operator === "lte") return "range";
  if (selected.operator === "between" && selected.values.length === 2) {
    const [start, end] = selected.values;
    if (isYearSpan(start, end)) return "year";
    if (isMonthSpan(start, end)) return "month";
    return "range";
  }
  return "exact";
};

const DateColumnFilter = ({ fieldName, formatter, get, selected, onChange }: ColumnFilterProps) => {
  const i18n = useI18n();
  const [mode, setMode] = useState<Mode>(() => deriveMode(selected));
  const [rangeFrom, setRangeFrom] = useState<string | null>(
    selected.operator === "gte" || (selected.operator === "between" && typeof selected.values[0] === "string")
      ? ((selected.values[0] as string) ?? null)
      : null,
  );
  const [rangeTo, setRangeTo] = useState<string | null>(
    selected.operator === "lte"
      ? ((selected.values[0] as string) ?? null)
      : selected.operator === "between" && typeof selected.values[1] === "string"
        ? ((selected.values[1] as string) ?? null)
        : null,
  );
  const { options } = useDistinctValues(get, fieldName, selected.values);

  const selectMode = (nextMode: Mode) => {
    setMode(nextMode);
    setRangeFrom(null);
    setRangeTo(null);
    onChange({ operator: undefined, values: [] });
  };

  const toggleExact = (option: Option) =>
    onChange({ operator: undefined, values: toggleListOption(selected.values, option) });

  const onMonthChange = (raw: string) => {
    const match = /^(\d{4})-(\d{2})$/.exec(raw);
    if (!match) {
      onChange({ operator: undefined, values: [] });
      return;
    }
    onChange({ operator: "between", values: monthSpan(Number(match[1]), Number(match[2])) });
  };

  const onYearChange = (year: number | null) => {
    if (year === null) {
      onChange({ operator: undefined, values: [] });
      return;
    }
    onChange({ operator: "between", values: yearSpan(year) });
  };

  const commitRange = (from: string | null, to: string | null) => {
    if (from && to) {
      onChange({ operator: "between", values: [from, to] });
    } else if (from) {
      onChange({ operator: "gte", values: [from] });
    } else if (to) {
      onChange({ operator: "lte", values: [to] });
    } else {
      onChange({ operator: undefined, values: [] });
    }
  };

  const monthValue =
    mode === "month" && selected.operator === "between" && typeof selected.values[0] === "string"
      ? selected.values[0].slice(0, 7)
      : "";

  const yearValue =
    mode === "year" && selected.operator === "between" && typeof selected.values[0] === "string"
      ? selected.values[0].slice(0, 4)
      : "";

  return (
    <>
      <FilterModeSwitcher
        modes={[
          { value: "exact", label: i18n.t.common.date },
          { value: "month", label: i18n.t.common.filterMonth },
          { value: "year", label: i18n.t.common.year },
          { value: "range", label: i18n.t.common.filterRange },
        ]}
        active={mode}
        onSelect={selectMode}
      />

      {mode === "exact" && <FilterOptionList options={options} formatter={formatter} onToggle={toggleExact} />}

      {mode === "month" && (
        <FilterValueSlot>
          <MonthYearPicker value={monthValue} clearable onChange={onMonthChange} />
        </FilterValueSlot>
      )}

      {mode === "year" && (
        <FilterValueSlot>
          <Numeric autoFocus hideThousandSeparator value={yearValue || undefined} onBlur={onYearChange} />
        </FilterValueSlot>
      )}

      {mode === "range" && (
        <FilterRangeInputs
          fromLabel={i18n.t.common.from}
          toLabel={i18n.t.common.to}
          from={
            <DatePicker
              value={rangeFrom ?? ""}
              clearable
              onChange={(date) => {
                const next = date || null;
                setRangeFrom(next);
                commitRange(next, rangeTo);
              }}
            />
          }
          to={
            <DatePicker
              value={rangeTo ?? ""}
              clearable
              onChange={(date) => {
                const next = date || null;
                setRangeTo(next);
                commitRange(rangeFrom, next);
              }}
            />
          }
        />
      )}
    </>
  );
};

export default DateColumnFilter;
